import {
  Box,
  Button,
  HStack,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Textarea,
  useDisclosure,
  useToast
} from "@chakra-ui/react";
import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { PiWarningCircleDuotone, PiKeyDuotone } from "react-icons/pi";
import { createBugReport, requestApiAccess } from "@lubbezposrednio-frontend/core";

// Wiersz akcji w sidebarze — wygląda jak NavRow, ale wyzwala modal (nie nawiguje).
const ActionRow: React.FC<{ label: string; icon: React.ElementType; color?: string; onClick: () => void }> = ({ label, icon, color = "#430098", onClick }) => (
  <HStack
    as="button"
    type="button"
    onClick={onClick}
    w="100%"
    spacing="13px"
    px="12px"
    py="9px"
    borderRadius="12px"
    transition="all 0.16s cubic-bezier(0.22, 1, 0.36, 1)"
    _hover={{ bg: "rgba(24,12,48,0.035)" }}
  >
    <Icon as={icon} boxSize="21px" color={color} flexShrink={0} />
    <Text fontSize="14.5px" fontWeight="600" color="#4a4458">{label}</Text>
  </HStack>
);

const BugReportModal: React.FC<{ isOpen: boolean; onClose: () => void }> = ({ isOpen, onClose }) => {
  const toast = useToast();
  const { pathname } = useLocation();
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const submit = async () => {
    const value = message.trim();
    if (value.length < 5) return;
    setSending(true);
    try {
      await createBugReport(value, pathname, "web");
      toast({ title: "Dziękujemy! Zgłoszenie trafiło do zespołu.", status: "success", isClosable: true, duration: 3500 });
      setMessage("");
      onClose();
    } catch {
      toast({ title: "Nie udało się wysłać zgłoszenia", status: "error", isClosable: true });
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
      <ModalOverlay bg="rgba(20,0,45,0.55)" backdropFilter="blur(3px)" />
      <ModalContent borderRadius="20px" overflow="hidden">
        <ModalHeader borderBottom="1px solid rgba(67,0,152,0.08)" fontSize="17px" color="primary" fontWeight="800">
          <HStack spacing="9px">
            <Icon as={PiWarningCircleDuotone} color="accent" />
            <Text>Zgłoś błąd</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pt="16px">
          <Text fontSize="13.5px" color="gray.500" mb="12px">
            Opisz, co poszło nie tak. Dołączymy adres ekranu, na którym jesteś, oraz Twój e-mail — nie musisz ich wpisywać.
          </Text>
          <Textarea
            autoFocus
            value={message}
            onChange={e => setMessage(e.target.value)}
            placeholder="Np. „Po kliknięciu Opublikuj nic się nie dzieje, a lista głosowań jest pusta…”"
            minH="150px"
            fontSize="15px"
            borderColor="rgba(67,0,152,0.18)"
            _focusVisible={{ borderColor: "primary", boxShadow: "0 0 0 1px var(--chakra-colors-primary)" }}
            maxLength={5000}
          />
          <Text fontSize="11.5px" color="gray.400" mt="6px" textAlign="right">{message.length}/5000</Text>
        </ModalBody>
        <ModalFooter gap="10px">
          <Button variant="ghost" onClick={onClose} borderRadius="11px" fontWeight="700">
            Anuluj
          </Button>
          <Button onClick={submit} isLoading={sending} isDisabled={message.trim().length < 5} bg="accent" color="white" borderRadius="11px" fontWeight="800" _hover={{ bg: "#c41a3a" }}>
            Wyślij zgłoszenie
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const ApiRequestModal: React.FC<{ isOpen: boolean; onClose: () => void }> = ({ isOpen, onClose }) => {
  const toast = useToast();
  const [justification, setJustification] = useState("");
  const [sending, setSending] = useState(false);

  const submit = async () => {
    const value = justification.trim();
    if (value.length < 5) return;
    setSending(true);
    try {
      await requestApiAccess(value);
      toast({ title: "Wniosek wysłany — odezwiemy się po jego rozpatrzeniu.", status: "success", isClosable: true, duration: 4000 });
      setJustification("");
      onClose();
    } catch (err: any) {
      const msg = err?.response?.data?.errors?.[0] || "Nie udało się wysłać wniosku";
      toast({ title: msg, status: "error", isClosable: true });
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
      <ModalOverlay bg="rgba(20,0,45,0.55)" backdropFilter="blur(3px)" />
      <ModalContent borderRadius="20px" overflow="hidden">
        <ModalHeader borderBottom="1px solid rgba(67,0,152,0.08)" fontSize="17px" color="primary" fontWeight="800">
          <HStack spacing="9px">
            <Icon as={PiKeyDuotone} color="primary" />
            <Text>Wniosek o dostęp do API</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pt="16px">
          <Text fontSize="13.5px" color="gray.500" mb="12px">
            Opisz, do czego potrzebujesz dostępu do naszego API (np. integracja z własnym systemem). Po akceptacji odblokujemy dokumentację i klucz API w panelu.
          </Text>
          <Textarea
            autoFocus
            value={justification}
            onChange={e => setJustification(e.target.value)}
            placeholder="Np. „Chcemy automatycznie tworzyć głosowania z naszego systemu członkowskiego i pobierać wyniki…”"
            minH="140px"
            fontSize="15px"
            borderColor="rgba(67,0,152,0.18)"
            _focusVisible={{ borderColor: "primary", boxShadow: "0 0 0 1px var(--chakra-colors-primary)" }}
            maxLength={5000}
          />
          <Text fontSize="11.5px" color="gray.400" mt="6px" textAlign="right">{justification.length}/5000</Text>
        </ModalBody>
        <ModalFooter gap="10px">
          <Button variant="ghost" onClick={onClose} borderRadius="11px" fontWeight="700">
            Anuluj
          </Button>
          <Button onClick={submit} isLoading={sending} isDisabled={justification.trim().length < 5} bg="primary" color="white" borderRadius="11px" fontWeight="800" _hover={{ bg: "#360079" }}>
            Wyślij wniosek
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

/**
 * Akcje wsparcia w sidebarze (za logowaniem), pod „Eksplorer bloków":
 *  - Zgłoś błąd — zawsze
 *  - Wniosek o API — tylko gdy konto nie ma jeszcze dostępu do API
 */
export const SupportActions: React.FC<{ apiAccess: boolean }> = ({ apiAccess }) => {
  const bug = useDisclosure();
  const api = useDisclosure();

  return (
    <Box>
      <ActionRow label="Zgłoś błąd" icon={PiWarningCircleDuotone} color="#ef4444" onClick={bug.onOpen} />
      {!apiAccess && <ActionRow label="Wniosek o API" icon={PiKeyDuotone} color="#f59e0b" onClick={api.onOpen} />}
      <BugReportModal isOpen={bug.isOpen} onClose={bug.onClose} />
      <ApiRequestModal isOpen={api.isOpen} onClose={api.onClose} />
    </Box>
  );
};
